import { useIntersection } from '@mantine/hooks';
import { useRef, useEffect, useState } from 'react';

export interface UseInViewOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export interface UseInViewReturn<T extends HTMLElement> {
  ref: (element: T | null) => void;
  inView: boolean;
  entry: IntersectionObserverEntry | null;
}

export function useInView<T extends HTMLElement = HTMLDivElement>(
  options: UseInViewOptions = {}
): UseInViewReturn<T> {
  const { threshold = 0, rootMargin = '0px', triggerOnce = false } = options;
  const [hasTriggered, setHasTriggered] = useState(false);

  const { ref, entry } = useIntersection<T>({
    threshold,
    rootMargin,
  });

  const inView = entry?.isIntersecting ?? false;

  useEffect(() => {
    if (inView && triggerOnce && !hasTriggered) {
      setHasTriggered(true);
    }
  }, [inView, triggerOnce, hasTriggered]);

  return {
    ref,
    inView: triggerOnce ? (hasTriggered || inView) : inView,
    entry: entry ?? null,
  };
}

export default useInView;
